import { Button, Input, PageTitle } from '../../components';
import { Card, CardBody, Col, Form, FormGroup, InputGroup, InputGroupText, Label, Modal, Row } from 'reactstrap';
import React, { useEffect, useState } from 'react';

import { AmsPermissionClaimType, authService } from '../../services/Auth';
import { Institution } from '../../common/Types';
import { Link } from 'react-router-dom';
import NewInstitution from '../../components/NewInstitution';
import Skeleton from 'react-loading-skeleton';
import { institutionService } from '../../services/InstitutionService';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

const Institutions = () => {
    // List of active institutions
    const [institutions, setInstitutions] = useState<Institution[]>();
    // Seperate list to track inactive institutions
    const [inactiveInstitutions, setInactiveInstitutions] = useState<Institution[]>([]);
    // Tracking original list to reset ui after filtering
    const [originalInstitutions, setOriginalInstitutions] = useState<Institution[]>();
    const [searchText, setSearchText] = useState<string>('');
    const [searchId, setSearchId] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);

    const [showNewInstitution, setShowNewInstitution] = useState<boolean>(false);
    const toggleShowNewInstitution = () => setShowNewInstitution(!showNewInstitution);

    const canCreateInstitution = authService.hasPermission(AmsPermissionClaimType, 'systemadmin');

    const handleInstitutionSaved = () => {
        setLoading(true);
        institutionService
            .getInstitutions()
            .then((response) => {
                setOriginalInstitutions(response);
                setInstitutions(response.filter((x) => x.active));
                setInactiveInstitutions(response.filter((x) => !x.active));
                toggleShowNewInstitution();
                toast.success('Institution saved successfully');
            })
            .catch(() => {
                toast.error('Failed to refresh institution list.');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const { handleSubmit } = useForm({});

    useEffect(() => {
        const getInstitutions = () => {
            setLoading(true);
            institutionService
                .getInstitutions()
                .then((response) => {
                    // Use a single pass to filter instead of multiple
                    const active: Institution[] = [];
                    const inactive: Institution[] = [];
                    
                    for (const institution of response) {
                        if (institution.active) {
                            active.push(institution);
                        } else {
                            inactive.push(institution);
                        }
                    }
                    
                    setOriginalInstitutions(response);
                    setInstitutions(active);
                    setInactiveInstitutions(inactive);
                })
                .catch(() => {
                    toast.error('Failed to get institution list.');
                })
                .finally(() => {
                    setLoading(false);
                });
        };

        getInstitutions();
    }, [setInstitutions, setOriginalInstitutions]);

    const onSearchSubmit = () => {
        if (searchText !== '' && searchText && originalInstitutions) {
            const searchLower = searchText.toLowerCase();
            const filteredList = originalInstitutions.filter((x) => {
                // Check main title first (most common case)
                if (x.title.toLowerCase().includes(searchLower)) {
                    return true;
                }
                // Check name history only if needed
                return (x.nameHistory || []).some(h => 
                    h.title.toLowerCase().includes(searchLower)
                );
            });
            setInstitutions(filteredList);
        } else {
            setInstitutions(originalInstitutions?.filter(x => x.active) || []);
        }
    };

    const onIdSearchSubmit = () => {
        if (searchId && !isNaN(+searchId) && originalInstitutions) {
            const searchIdNum = +searchId;
            const institution = originalInstitutions.find((x) => x.institutionId === searchIdNum);
            setInstitutions(institution ? [institution] : []);
        } else {
            setInstitutions(originalInstitutions?.filter(x => x.active) || []);
        }
    };

    const renderInstitutionLink = (institution: Institution) => {
        return (
            <div key={institution.institutionId} style={{ flexBasis: `calc(25% - 10px)` }}>
                <div>
                    <Link className="link-to-institute" to={`/institution/${institution.institutionId}`}>
                        <div className={'d-flex flex-column'}>
                            <Label className={'mb-0 pb-0'} style={{ cursor: 'pointer' }}>
                                {institution.title}
                            </Label>
                            <span
                                className={'text-muted mt-0 pt-0 pl-2'}
                            >{`${institution.city}, ${institution.state}`}</span>
                        </div>
                    </Link>
                </div>
            </div>
        );
    };

    return (
        <>
            <PageTitle className={`d-flex justify-content-between py-2`}>
                <h4>{`Institutions`}</h4>
                {canCreateInstitution && (
                    <Button type={'button'} color={'primary'} onClick={() => setShowNewInstitution(true)}>
                        <i className={'mdi mdi-plus'} />
                        <span className={'ml-1'}>{`New Institution`}</span>
                    </Button>
                )}
            </PageTitle>
            <Row>
                <Col>
                    <Card>
                        <CardBody>
                            <Row className="mb-3">
                                <Col className={'d-flex justify-content-between'}>
                                    <Form
                                        noValidate
                                        onSubmit={handleSubmit(onSearchSubmit)}
                                        style={{ flexBasis: 'calc(50% - 20px)' }}
                                    >
                                        <FormGroup>
                                            <Label>{`Search by Name`}</Label>
                                            <InputGroup>
                                                <Input
                                                    placeholder="Institution Name"
                                                    type="text"
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                />
                                                <InputGroupText>
                                                    <Button color="primary" type="submit">
                                                        {`Search`}
                                                    </Button>
                                                    <Button
                                                        color="white"
                                                        className={'ml-2 btn-link'}
                                                        type="submit"
                                                        onClick={(e) => setSearchText('')}
                                                    >
                                                        {`Reset`}
                                                    </Button>
                                                </InputGroupText>
                                            </InputGroup>
                                        </FormGroup>
                                    </Form>
                                    <Form
                                        noValidate
                                        onSubmit={handleSubmit(onIdSearchSubmit)}
                                        style={{ flexBasis: 'calc(25% - 20px)' }}
                                    >
                                        <FormGroup>
                                            <Label>{`Search by ID`}</Label>
                                            <InputGroup>
                                                <Input
                                                    placeholder="Institution ID"
                                                    type={'text'}
                                                    value={searchId}
                                                    onChange={(e) => setSearchId(e.target.value)}
                                                />
                                                <InputGroupText>
                                                    <Button color="primary" type="submit">
                                                        {`Search`}
                                                    </Button>
                                                    <Button
                                                        color="white"
                                                        className={'ml-2 btn-link'}
                                                        type="submit"
                                                        onClick={(e) => setSearchId('')}
                                                    >
                                                        {`Reset`}
                                                    </Button>
                                                </InputGroupText>
                                            </InputGroup>
                                        </FormGroup>
                                    </Form>
                                </Col>
                            </Row>

                            {!loading && institutions && (
                                <Row>
                                    <Col className={'d-flex flex-wrap justify-content-between'}>
                                        {institutions.map((institution) => renderInstitutionLink(institution))}
                                    </Col>
                                </Row>
                            )}
                            {loading && (
                                <>
                                    <Row>
                                        <Col lg={3} md={6}>
                                            <Skeleton count={5} height={40} />
                                        </Col>
                                        <Col lg={3} md={6}>
                                            <Skeleton count={5} height={40} />
                                        </Col>
                                        <Col lg={3} md={6}>
                                            <Skeleton count={5} height={40} />
                                        </Col>
                                        <Col lg={3} md={6}>
                                            <Skeleton count={5} height={40} />
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </CardBody>
                    </Card>
                    {inactiveInstitutions?.length > 0 && (
                        <Card>
                            <CardBody>
                                <Row>
                                    <Col xs={12}>
                                        <h5>Inactive Institutions</h5>
                                    </Col>
                                </Row>

                                {inactiveInstitutions && (
                                    <Row>
                                        <Col className={'d-flex flex-wrap justify-content-between'}>
                                            {inactiveInstitutions.map((institution) =>
                                                renderInstitutionLink(institution),
                                            )}
                                        </Col>
                                    </Row>
                                )}
                            </CardBody>
                        </Card>
                    )}
                </Col>
            </Row>
            {canCreateInstitution && (
                <Modal isOpen={showNewInstitution} toggle={toggleShowNewInstitution} size={'lg'}>
                    <NewInstitution onCanceled={toggleShowNewInstitution} onSaved={handleInstitutionSaved} />
                </Modal>
            )}
        </>
    );
};

export default Institutions;
